import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { EntityChip } from '../components/EntityChip.js';
import { Chip } from '../components/Chip.js';
import { Icon } from '../components/Icon.js';
import { EnrichmentFields } from '../components/EnrichmentFields.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { eventTypeById } from '../state/selectors.js';
import { useAppStore } from '../state/store.js';
import { navigateToObject, navigate, navigateToObjectKind } from '../state/hashRouter.js';
import { EVENT_TYPE_ATTRIBUTE_DOCS } from '../state/attributeGlossary.js';
import { WorkflowTrail } from './WorkflowTrail.js';
import { PresenceContainer } from './PresenceContainer.js';
import { AnnotationsContainer } from './AnnotationsContainer.js';
import { AskAtlasToEditBox } from '../components/AskAtlasToEditBox.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface EventTypeDetailViewProps {
  manifest: AtlasManifest;
  eventType: string;
}

export function EventTypeDetailView({ manifest, eventType }: EventTypeDetailViewProps): React.JSX.Element {
  const ev = eventTypeById(manifest, eventType);
  if (!ev) {
    return <EmptyState icon="help" title={`Event ${eventType} not found.`} />;
  }

  const { entityTrail } = useAppStore();

  return (
    <div className="detail-view">
      <ObjectHeader
        object={ev}
        icon="event"
        iconLabel="Event type"
        badges={[]}
        title={ev.type}
        docRef={<>{ev.category} · schema v{ev.schemaVersion}</>}
        description={ev.description}
        chips={ev.complianceRef ? [{ label: ev.complianceRef, tone: 'caution' as const }] : []}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'sandbox', seedObject: { kind: 'event-type', id: ev.type } })}>
              <Icon name="edit" size="sm" />
              Edit in Sandbox
            </button>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'ask', focus: ev.type })}>
              <Icon name="auto_awesome" size="sm" />
              Ask Atlas
            </button>
          </>
        }
      />
      <dl className="sheet-hero-stats" aria-label="stats">
        <div>
          <dt>CARRIED ENTITIES</dt>
          <dd>{ev.carriedEntities.length}</dd>
        </div>
        <div>
          <dt>EMITTED BY</dt>
          <dd>{(ev.emittedBy ?? []).length}</dd>
        </div>
      </dl>
      <EnrichmentFields
        fields={[
          { key: 'requiresDualWrite', label: 'Requires Dual Write', value: ev.requiresDualWrite, glossary: EVENT_TYPE_ATTRIBUTE_DOCS['requiresDualWrite'] },
          { key: 'retentionDays', label: 'Retention (days)', value: ev.retentionDays, glossary: EVENT_TYPE_ATTRIBUTE_DOCS['retentionDays'] },
          { key: 'businessTrigger', label: 'Business Trigger', value: ev.businessTrigger, glossary: EVENT_TYPE_ATTRIBUTE_DOCS['businessTrigger'] },
          { key: 'lifecycle', label: 'Lifecycle', value: ev.lifecycle, glossary: EVENT_TYPE_ATTRIBUTE_DOCS['lifecycle'] },
        ]}
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <PresenceContainer entityId={ev.type} />
      <AskAtlasToEditBox
        kind="event-type"
        selectorId={ev.type}
        currentObject={ev as unknown as Record<string, unknown>}
        manifest={manifest}
        onAsk={(opts) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds)}
      />
      {ev.aiEnrichedFields && ev.aiEnrichedFields.includes('payloadFields') && (
        <div className="enrichment-field enrichment-field--ai" style={{ marginBottom: 'var(--sp-sm)' }}>
          <span className="enrichment-label">AI enriched</span>
          <span className="enrichment-ai-badge" title="Payload fields drafted by AI — not authoritative until reviewed">
            ✦ payloadFields
          </span>
        </div>
      )}
      <section style={{ marginBottom: 'var(--sp-xl)' }}>
        <div className="section-label">Payload · {ev.payloadFields.length} fields</div>
        {ev.payloadFields.length > 0 ? (
          <table className="field-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {ev.payloadFields.map((f) => (
                <tr key={f.name}>
                  <td>{f.name}</td>
                  <td>{f.type}</td>
                  <td style={{ color: 'var(--text3)' }}>{f.description ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No payload schema catalogued yet.</p>
        )}
      </section>
      <section style={{ marginBottom: 'var(--sp-lg)' }}>
        <div className="section-label">
          Carried entities · {ev.carriedEntities.length}
          {ev.carriedEntitiesInferred && <span style={{ color: 'var(--text3)' }}> (inferred)</span>}
        </div>
        {ev.carriedEntities.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {ev.carriedEntities.map((entityId) => (
              <EntityChip key={entityId} entityId={entityId} manifest={manifest} />
            ))}
          </div>
        ) : (
          <p>None</p>
        )}
      </section>
      {ev.emittedBy && ev.emittedBy.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">Emitted by · {ev.emittedBy.length} functions</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {ev.emittedBy.map((functionName) => (
              <Chip
                key={functionName}
                label={functionName}
                onClick={() => navigate({ kind: 'cloud-function', id: functionName })}
              />
            ))}
          </div>
        </section>
      )}
      {((ev.consumerSurfaces?.length ?? 0) > 0 || (ev.consumerFunctions?.length ?? 0) > 0) && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">
            Consumers · {(ev.consumerSurfaces?.length ?? 0) + (ev.consumerFunctions?.length ?? 0)}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {(ev.consumerSurfaces ?? []).map((surfaceId) => (
              <Chip
                key={`surface-${surfaceId}`}
                label={surfaceId}
                onClick={() => navigate({ kind: 'surface', id: surfaceId })}
              />
            ))}
            {(ev.consumerFunctions ?? []).map((functionName) => (
              <Chip
                key={`function-${functionName}`}
                label={functionName}
                onClick={() => navigate({ kind: 'cloud-function', id: functionName })}
              />
            ))}
          </div>
        </section>
      )}
      <RelationshipsPanel manifest={manifest} kind="event-type" id={ev.type} onNavigate={navigateToObjectKind} />
      <AnnotationsContainer entityId={ev.type} />
    </div>
  );
}
